import { from, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import {
  Properties,
  Property,
  dispatchForm,
  PaginationState,
  CommonForm,
  MediaTypes,
  AccessControl,
} from '@lib/state';
import {
  MaintenanceTicketStore,
  maintenanceTicketStore,
  MaintenanceTicketUIState,
} from './maintenance-ticket.store';
import { applyTransaction } from '@datorama/akita';
import { lightFormat } from 'date-fns';
import {
  MaintenanceTicket,
  MaintenanceTicketPhoto,
  PriorityOptions,
  TicketStatus,
  CreationSource,
} from './maintenance-ticket.model';
import {
  CreateMaintenanceTicketRequest,
  MaintenanceTicketModel,
  UpdateMaintenanceTicketRequest,
} from '@lib/state/api/generated/properties';
import { MaintenanceTicketForms } from './maintenance-ticket.form';
import { MaintenanceTicketRoomEntryCodeModel } from '@lib/state/api/generated/access-control';

export class MaintenanceTicketService {
  private duplicateSearch$ = new Subject<{
    propertyId?: string;
    location?: string;
    continuationToken?: string;
  }>();

  constructor(
    private readonly store: MaintenanceTicketStore,
    private readonly maintenanceApi: Properties.MaintenanceApi,
    private readonly entryCodeApi: AccessControl.EntryCodeApi
  ) {
    this.duplicateSearch$
      .pipe(
        distinctUntilChanged(
          (a, b) =>
            a.location === b.location &&
            a.propertyId === b.propertyId &&
            a.continuationToken === b.continuationToken
        ),
        debounceTime(500),
        switchMap(filters => {
          // No-op for invalid filters
          if (
            !filters.propertyId ||
            filters.propertyId === '' ||
            !filters.location ||
            filters.location === ''
          ) {
            return of({
              data: new Array<MaintenanceTicketModel>(),
              pagination: undefined,
              concat: false,
            });
          }

          return from(
            this.maintenanceApi.maintenanceGet(
              filters.propertyId,
              filters.continuationToken,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              5,
              true,
              undefined,
              undefined,
              filters.location
            )
          ).pipe(
            map(({ data }) => ({
              data: data.data,
              pagination: { continuationToken: data.continuationToken, isDone: data.isDone },
              concat: !!filters.continuationToken,
            })),
            catchError(() =>
              of({
                data: new Array<MaintenanceTicketModel>(),
                // We'll assume pagination is done on error for now.
                pagination: { isDone: true },
                concat: !!filters.continuationToken,
              })
            )
          );
        })
      )
      .subscribe(data => {
        applyTransaction(() => {
          this.store.update(({ ui }) => ({
            ui: {
              ...ui,
              duplicateTickets: data.concat
                ? [...(ui.duplicateTickets ?? []), ...data.data]
                : data.data,
              duplicateTicketPaginationState: data.pagination,
            },
          }));
        });
      });
  }

  getDuplicateMainenanceTickets(propertyId: string, location?: string, continuationToken?: string) {
    this.duplicateSearch$.next({ propertyId, location, continuationToken });
  }

  getMaintenanceTickets(
    propertyId: Property['id'],
    startDate?: Date,
    endDate?: Date,
    status?: TicketStatus,
    assignedTo?: string,
    priority?: PriorityOptions,
    active?: boolean,
    textFilter?: string,
    location?: string,
    creationSource?: CreationSource,
    continuationToken?: string | null,
    limit?: number
  ) {
    from(
      this.maintenanceApi.maintenanceGet(
        propertyId,
        continuationToken ?? undefined,
        startDate ? lightFormat(startDate, 'yyyy-MM-dd') : undefined,
        endDate ? lightFormat(endDate, 'yyyy-MM-dd') : undefined,
        status,
        assignedTo ?? undefined,
        priority,
        limit,
        undefined,
        active,
        textFilter,
        location,
        creationSource
      )
    )
      .pipe(map(({ data }) => data))
      .subscribe(({ data, ...pagination }) =>
        applyTransaction(() => {
          this.store.upsertMany(data);
          this.updateUI({
            propertyId,
            startDate,
            endDate,
            status,
            assignedTo,
            priority,
            active,
            textFilter,
            location,
            creationSource,
          });
          this.updatePaginationState(pagination);
        })
      );
  }

  getMaintenanceTicketById(propertyId: Property['id'], id: MaintenanceTicket['id']) {
    from(this.maintenanceApi.maintenancePropertyIdTicketsIdGet(propertyId, id))
      .pipe(
        map(({ data }) => data.data),
        switchMap(ticket => {
          if (!ticket.roomId) {
            return of({ ticket, entryCode: undefined });
          }
          return from(
            this.entryCodeApi.entrycodePropertyIdMaintenanceMaintenanceTicketIdGet(
              propertyId,
              ticket.id
            )
          ).pipe(
            map(({ data }) => {
              return {
                ticket,
                entryCode: data.data,
              };
            })
          );
        })
      )
      .subscribe(({ ticket, entryCode }) => {
        this.updateStore(ticket, entryCode);
      });
  }

  createMaintenanceTicket(request: CreateMaintenanceTicketRequest) {
    from(this.maintenanceApi.maintenancePost(request))
      .pipe(
        dispatchForm(MaintenanceTicketForms.Manage),
        map(({ data }) => data.data),
        switchMap(ticket => this.handleEntryCodeGeneration(request.propertyId, ticket))
      )
      .subscribe(({ ticket, entryCode }) => {
        applyTransaction(() => {
          this.updateStore(ticket, entryCode);
        });
      });
  }

  getMaintenanceTicketPhotos(id: MaintenanceTicket['id']) {
    from(this.maintenanceApi.maintenanceIdPhotoListGet(id))
      .pipe(map(({ data }) => data.data))
      .subscribe(photos => {
        applyTransaction(() => {
          this.updatePhotosState(photos);
        });
      });
  }

  loadCategories() {
    from(this.maintenanceApi.maintenanceCategoriesGet())
      .pipe(map(({ data }) => data.data))
      .subscribe(categories => {
        this.updateUI({ categories });
      });
  }

  createMaintenanceTicketPhotos(id: string, files: File) {
    from(this.maintenanceApi.maintenanceIdPhotoUploadPost(id, files))
      .pipe(
        dispatchForm(MaintenanceTicketForms.CreatePhoto),
        map(({ data }) => data.data)
      )
      .subscribe(photos => {
        applyTransaction(() => {
          this.updatePhotosStateAdd(photos);
        });
      });
  }

  updateMaintenanceTicket(
    requestTicket: MaintenanceTicket,
    request: UpdateMaintenanceTicketRequest
  ) {
    from(this.maintenanceApi.maintenanceIdPost(requestTicket.id, request))
      .pipe(
        dispatchForm(MaintenanceTicketForms.Manage),
        map(({ data }) => data.data),
        switchMap(ticket => {
          // Check if entry code regeneration is needed
          const shouldRegenerateCode = this.shouldRegenerateEntryCode(requestTicket, request);

          if (shouldRegenerateCode) {
            return this.handleEntryCodeGeneration(request.propertyId, ticket);
          }

          return from(
            this.entryCodeApi.entrycodePropertyIdMaintenanceMaintenanceTicketIdGet(
              request.propertyId,
              ticket.id
            )
          ).pipe(map(({ data }) => ({ ticket, entryCode: data.data })));
        })
      )
      .subscribe(({ ticket, entryCode }) => {
        this.updateStore(ticket, entryCode);
      });
  }

  grantMaintenanceTicketAccess(propertyId: Property['id'], ticket: MaintenanceTicket) {
    from(
      this.entryCodeApi.entrycodePropertyIdMaintenanceMaintenanceTicketIdPost(
        propertyId,
        ticket.id,
        {
          roomNumber: ticket.roomNumber!,
          roomId: ticket.roomId!,
          userId: ticket.assignedTo?.id,
        }
      )
    )
      .pipe(
        dispatchForm(MaintenanceTicketForms.GrantAccess),
        switchMap(() => {
          return from(
            this.entryCodeApi.entrycodePropertyIdMaintenanceMaintenanceTicketIdGet(
              propertyId,
              ticket.id
            )
          ).pipe(map(({ data }) => data.data));
        })
      )
      .subscribe(entryCode => {
        this.store.update(() => ({
          entryCode: entryCode,
        }));
      });
  }

  deleteMaintenanceTicket(requestTicket: MaintenanceTicket) {
    from(this.maintenanceApi.maintenanceIdDeletePost(requestTicket.id))
      .pipe(
        dispatchForm(MaintenanceTicketForms.Delete),
        map(({ data }) => data.data),
        switchMap(ticket => {
          return this.handleEntryCodeGeneration(requestTicket.propertyId, ticket);
        })
      )
      .subscribe(({ ticket, entryCode }) => {
        this.updateStore(ticket, entryCode);
      });
  }

  deleteMaintenanceTicketPhoto(id: MaintenanceTicket['id'], photoId: MaintenanceTicketPhoto['id']) {
    from(this.maintenanceApi.maintenanceIdPhotoPhotoIdDeletePost(id, photoId))
      .pipe(dispatchForm(MaintenanceTicketForms.DeletePhoto))
      .subscribe(() => {
        this.updatePhotosStateDelete(photoId);
      });
  }

  exportMaintenanceTickets(
    propertyId: Property['id'],
    startDate?: Date,
    endDate?: Date,
    assignedTo?: string,
    status?: TicketStatus,
    priority?: PriorityOptions,
    active?: boolean,
    creationSource?: CreationSource
  ) {
    from(
      this.maintenanceApi.maintenanceExportGet(
        propertyId,
        undefined,
        startDate ? lightFormat(startDate, 'yyyy-MM-dd') : undefined,
        endDate ? lightFormat(endDate, 'yyyy-MM-dd') : undefined,
        status,
        assignedTo,
        priority,
        undefined,
        undefined,
        active,
        undefined,
        undefined,
        creationSource,
        {
          responseType: 'arraybuffer', // Pass 'arraybuffer' as responseType in options
        }
      )
    )
      .pipe(
        map(x => new Blob([x.data], { type: MediaTypes.ExcelFile })),
        dispatchForm(CommonForm.Export)
      )
      .subscribe(x => saveAs(x, `Maintenance_Tickets_${new Date().valueOf()}.xlsx`));
  }

  setActive(id?: string) {
    this.store.setActive(id ?? null);
  }

  resetDuplicateTickets() {
    this.duplicateSearch$.next({});
  }

  private updatePaginationState(
    { isDone, continuationToken }: PaginationState,
    key: 'pagination' | 'current' | 'future' = 'pagination'
  ) {
    this.store.update(() => ({
      [key]: { isDone, continuationToken },
    }));
  }

  private updatePhotosState(photos?: MaintenanceTicketPhoto[]) {
    this.store.update(() => ({ photos: photos }));
  }

  private updatePhotosStateAdd(photos: MaintenanceTicketPhoto[] = []) {
    let statePhotos = this.store.getValue().photos ?? [];
    statePhotos = [...statePhotos, ...photos];
    this.store.update(() => ({ photos: statePhotos }));
  }

  private updatePhotosStateDelete(photoId: string) {
    let statePhotos = this.store.getValue().photos;
    statePhotos = statePhotos?.filter(x => x.id !== photoId);

    this.store.update(() => ({ photos: statePhotos }));
  }

  private updateUI(state: Partial<MaintenanceTicketUIState>) {
    this.store.update(({ ui }) => ({
      ui: { ...ui, ...state },
    }));
  }

  private updateStore(
    ticket: MaintenanceTicketModel,
    entryCode?: MaintenanceTicketRoomEntryCodeModel
  ) {
    applyTransaction(() => {
      this.store.upsert(ticket.id, ticket);
      this.store.setActive(ticket.id);
      this.store.update(() => ({ entryCode: entryCode }));
    });
  }

  private handleEntryCodeGeneration(propertyId: string, ticket: MaintenanceTicketModel) {
    // Generate entry code if room is set
    if (ticket.roomId) {
      return from(
        this.entryCodeApi.entrycodePropertyIdMaintenanceMaintenanceTicketIdPost(
          propertyId,
          ticket.id,
          {
            roomNumber: ticket.roomNumber!,
            roomId: ticket.roomId,
            userId:
              ticket.status === TicketStatus.Closed ||
              ticket.status === TicketStatus.Completed ||
              ticket.isActive === false ||
              !!ticket.deletedOn
                ? undefined
                : ticket.assignedTo?.id,
          }
        )
      ).pipe(
        dispatchForm(MaintenanceTicketForms.GrantAccess),
        map(({ data: entryCode }) => ({
          ticket: ticket,
          entryCode: entryCode.data,
        }))
      );
    }

    return of({ ticket: ticket, entryCode: undefined });
  }

  private shouldRegenerateEntryCode(
    originalTicket: MaintenanceTicket,
    updateRequest: UpdateMaintenanceTicketRequest
  ): boolean {
    return (
      !!updateRequest.roomId &&
      (originalTicket.assignedTo?.id !== updateRequest.assignedTo ||
        originalTicket.roomId !== updateRequest.roomId ||
        updateRequest.status !== originalTicket.status ||
        updateRequest.isActive !== originalTicket.isActive)
    );
  }
}

export const maintenanceTicketService = new MaintenanceTicketService(
  maintenanceTicketStore,
  new Properties.MaintenanceApi(),
  new AccessControl.EntryCodeApi()
);
